import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Twitter as TwitterIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  getPlatformIcon, 
  getPlatformColor, 
  getPostTypeLabel, 
  getStatusColor 
} from '../../utils/platformUtils';

const PostCard = styled(Card)(({ theme, selected }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  border: selected ? `3px solid ${theme.palette.primary.main}` : '1px solid transparent',
  backgroundColor: selected ? theme.palette.action.selected : theme.palette.background.paper,
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    borderColor: selected ? theme.palette.primary.main : theme.palette.action.hover,
  },
  '&::after': selected ? {
    content: '""',
    position: 'absolute',
    top: -3,
    left: -3,
    right: -3,
    bottom: -3,
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 25%, transparent 25%)`,
    backgroundSize: '20px 20px',
    opacity: 0.1,
    pointerEvents: 'none',
    borderRadius: theme.shape.borderRadius,
  } : {},
}));

const PostContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const ContentPreview = styled(Typography)({
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  lineHeight: 1.5,
  height: '6em', // 4 lines * 1.5 line-height
});



const CampaignPostGrid = ({
  posts,
  selectedPosts,
  onSelectionChange,
  onPostEdit,
  onPostSave
}) => {
  const theme = useTheme();
  
  const handlePostSelect = (postId, event) => {
    event.stopPropagation();
    
    if (selectedPosts.includes(postId)) {
      onSelectionChange(selectedPosts.filter(id => id !== postId));
    } else {
      onSelectionChange([...selectedPosts, postId]);
    }
  };

  const handleEditClick = (post, event) => {
    event.stopPropagation();
    onPostEdit(post);
  };

  if (posts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No posts found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are no posts matching your current filter.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 3,
        }}
      >
        {posts.map((post) => {
          const isSelected = selectedPosts.includes(post.id);
          
          return (
            <PostCard
              key={post.id}
              selected={isSelected}
              onClick={(e) => handlePostSelect(post.id, e)}
              elevation={isSelected ? 4 : 1}
            >
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => handlePostSelect(post.id, e)}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    color="primary"
                    sx={{
                      '&.Mui-checked': {
                        color: 'primary.main',
                      }
                    }}
                  />
                  
                  <Avatar
                    sx={{
                      bgcolor: getPlatformColor(theme, post.platform),
                      width: 32,
                      height: 32,
                      mr: 1,
                      ...(isSelected && {
                        border: '2px solid',
                        borderColor: 'primary.main',
                      })
                    }}
                  >
                    {React.createElement(getPlatformIcon(post.platform) || TwitterIcon)}
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getPostTypeLabel(post.postType)}
                    </Typography>
                  </Box>

                  <Chip
                    label={post.status}
                    size="small"
                    color={getStatusColor(post.status)}
                    variant="outlined"
                  />
                </Box>

                <PostContent>
                  <ContentPreview variant="body2" color="text.primary">
                    {post.content}
                  </ContentPreview>
                </PostContent>

                {post.platforms && post.platforms.length > 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Also optimized for:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      {post.platforms
                        .filter(platform => platform !== post.platform)
                        .map((platform) => (
                          <Tooltip key={platform} title={platform}>
                            <Avatar
                              sx={{
                                bgcolor: getPlatformColor(theme, platform),
                                width: 20,
                                height: 20,
                                '& .MuiSvgIcon-root': { fontSize: 12 }
                              }}
                            >
                              {React.createElement(getPlatformIcon(platform) || TwitterIcon, { fontSize: 'small' })}
                            </Avatar>
                          </Tooltip>
                        ))
                      }
                    </Box>
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={(e) => handleEditClick(post, e)}
                >
                  Edit
                </Button>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {post.scheduledAt && (
                    <Tooltip title={`Scheduled for ${new Date(post.scheduledAt).toLocaleString()}`}>
                      <IconButton size="small">
                        <ScheduleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </CardActions>
            </PostCard>
          );
        })}
      </Box>
    </Box>
  );
};

export default CampaignPostGrid; 